// -----------------------------------------------------------------------------
// Central SEO configuration — the SINGLE source of truth for every canonical /
// Open-Graph / structured-data value in the app.
//
// DOMAIN PINNING (important): every absolute URL used for SEO is built ONLY from
// SITE_URL below. Nothing here reads `window.location`, `import.meta.env`, or the
// Vite build mode, so a dev / staging / preview deploy can NEVER leak its own
// origin into a <link rel="canonical">, an og:url, or a share image. Canonicals
// always point at production: https://www.jaaga.ai
//
// Keep this file pure (no JSX, no React, no import.meta) so it can also be
// imported by the Node build scripts in `scripts/`.
// -----------------------------------------------------------------------------

export const SITE_URL = "https://www.jaaga.ai";
export const SITE_NAME = "JaaGa";
export const LOCALE = "en_IN";

// Twitter/X handle used for twitter:site / twitter:creator. Fill in the real
// handle when available; the "@" is required.
export const TWITTER_HANDLE = "@jaaga_ai";

export const DEFAULT_TITLE =
  "JaaGa — Property Audit Report | Verify Any Property Before You Buy";

export const DEFAULT_DESCRIPTION =
  "Buying property in India? JaaGa audits every legal document — title, encumbrance (EC), litigation, RERA, property tax & 40+ government land records — and gives one clear verdict: is it safe to buy? Made in India.";

export const DEFAULT_KEYWORDS =
  "property audit report, is this property safe to buy, title verification, encumbrance certificate, EC, RTC pahani, 7/12 satbara, ROR, patta chitta, pattadhar passbook, adangal, sale deed, RERA check, land records, property documents India, mutation, property tax, legal opinion, made in india proptech";

// Default social share image (1200x630). Generated by scripts/generate-og-assets.mjs.
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.jpg`;

// Brand color (jaaga logo navy, tailwind `primary`) — theme-color / tile color.
export const THEME_COLOR = "#153568";

// Public contact + presence (only confirmed URLs; used in Organization sameAs).
export const CONTACT_PHONE = "+91-88851-66880";
export const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=app.jaaga.jaaga_pro1";
export const APP_STORE_URL =
  "https://apps.apple.com/us/app/jaaga-secure-your-property/id6737446238";
export const BLOG_URL = "https://blog.jaaga.ai";
export const PARTNER_URL = "https://partner.jaaga.ai";

// Official social / directory profiles. A consistent, complete `sameAs` set is
// the primary signal AI answer engines (ChatGPT, Gemini, Copilot, Perplexity)
// and Google's Knowledge Graph use to resolve "JaaGa" to ONE entity and trust
// facts about it. Keep this list in sync with the footer social links.
export const SOCIAL_PROFILES = [
  "https://www.linkedin.com/company/jaagarealestate/about/",
  "https://x.com/Jaaga_ai",
  "https://www.instagram.com/jaaga.ai/",
  "https://www.facebook.com/profile.php?id=61552907515347",
  "https://www.youtube.com/@jaagaapp",
];

// Topics JaaGa is authoritative on — `knowsAbout` helps AI models associate the
// brand with its niche when answering "who helps with property documents in India".
export const KNOWS_ABOUT = [
  "Property document verification in India",
  "Encumbrance Certificate (EC)",
  "Sale deed and property registration",
  "Land records (RTC, Pahani, Adangal, 7/12 Satbara, Patta Chitta, ROR)",
  "Property title verification and due diligence",
  "RERA project registration lookup",
  "Property mutation and property tax",
  "Property intelligence and ownership history",
  "Measuring land area and boundaries online",
  "Property survey number search and mapping",
  "Buying, selling, renting and leasing property in India",
  "Finding and locating lost or ancestral property in India",
  "Real estate legal opinion",
  "Property risk assessment for banks and lenders",
];

// The product pillars, as a schema.org OfferCatalog. Gives AI engines a
// structured answer to "what does JaaGa offer".
export const productCatalogJsonLd = () => ({
  "@type": "OfferCatalog",
  name: "JaaGa property platform",
  itemListElement: [
    {
      "@type": "Offer",
      itemOffered: {
        "@type": "Service",
        name: "JaaGa Documents",
        description:
          "Fetch 140+ government property documents online in minutes — sale deed, encumbrance certificate (EC), land records, RERA registration, mutation and property-tax records.",
        url: `${SITE_URL}/states`,
      },
    },
    {
      "@type": "Offer",
      itemOffered: {
        "@type": "Service",
        name: "JaaGa Services",
        description:
          "Done-for-you property paperwork: title verification, PTIN & Khata, encumbrance filing, mutation support and end-to-end registration.",
        url: `${SITE_URL}/services`,
      },
    },
    {
      "@type": "Offer",
      itemOffered: {
        "@type": "Service",
        name: "JaaGa Intelligence",
        description:
          "AI that reads billions of land records to reveal a property's ownership history, risk alerts, price trends and a Trust Score out of 100.",
        url: `${SITE_URL}/intelligence`,
      },
    },
    {
      "@type": "Offer",
      itemOffered: {
        "@type": "Service",
        name: "JaaGa Maps",
        description:
          "Search any land parcel by survey number, village and mandal on an interactive satellite map; measure area, check boundaries and save to a locker.",
        url: `${SITE_URL}/maps`,
      },
    },
  ],
});

// -----------------------------------------------------------------------------
// URL helpers
// -----------------------------------------------------------------------------

/**
 * Normalize any incoming link to an ABSOLUTE production URL on SITE_URL.
 * - "" / undefined            -> SITE_URL (home)
 * - "/telangana/ec"           -> "https://www.jaaga.ai/telangana/ec"
 * - "https://dev.jaaga.ai/x"  -> "https://www.jaaga.ai/x"   (origin re-pinned)
 * - "https://www.jaaga.ai/x"  -> unchanged
 * This is what guarantees a staging/dev origin can never end up in a canonical.
 */
export const toCanonicalUrl = (link) => {
  const raw = (link ?? "").toString().trim();
  if (!raw) return SITE_URL;
  let path = raw;
  // Strip any origin (http[s]://host) so only the path/query/hash survives.
  const originMatch = raw.match(/^https?:\/\/[^/]+(\/.*)?$/i);
  if (originMatch) path = originMatch[1] || "/";
  if (!path.startsWith("/")) path = `/${path}`;
  // Collapse accidental double slashes in the path (keep it clean).
  path = path.replace(/\/{2,}/g, "/");
  // Home stays clean (no trailing slash beyond the domain).
  if (path === "/") return SITE_URL;
  return `${SITE_URL}${path.replace(/\/$/, "")}`;
};

/** Absolute URL for an image path/URL, pinned to SITE_URL. */
export const toAbsoluteImage = (img) => {
  const raw = (img ?? "").toString().trim();
  if (!raw) return DEFAULT_OG_IMAGE;
  if (/^https?:\/\//i.test(raw)) return raw; // already absolute (e.g. a CDN asset)
  return `${SITE_URL}/${raw.replace(/^\//, "")}`;
};

// -----------------------------------------------------------------------------
// Structured data (JSON-LD) builders — schema.org
// -----------------------------------------------------------------------------

/** Organization node — powers Google's Knowledge Panel / brand rich results and
 *  is the primary machine-readable "entity home" AI answer engines read. */
export const organizationJsonLd = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${SITE_URL}/#organization`,
  name: SITE_NAME,
  legalName: "JaaGa Real Estate Services Private Limited",
  alternateName: ["JaaGa AI", "jaaga.ai", "JaaGa Real Estate"],
  url: SITE_URL,
  logo: `${SITE_URL}/icon-512.png`,
  image: DEFAULT_OG_IMAGE,
  description: DEFAULT_DESCRIPTION,
  slogan: "Know if a property is safe to buy",
  knowsAbout: KNOWS_ABOUT,
  areaServed: { "@type": "Country", name: "India" },
  knowsLanguage: ["en", "te", "hi"],
  contactPoint: [
    {
      "@type": "ContactPoint",
      telephone: CONTACT_PHONE,
      contactType: "customer support",
      areaServed: "IN",
      availableLanguage: ["en", "te", "hi"],
    },
  ],
  address: {
    "@type": "PostalAddress",
    addressCountry: "IN",
  },
  hasOfferCatalog: productCatalogJsonLd(),
  // Every canonical profile that resolves to the same entity. A complete,
  // consistent sameAs is how AI engines and the Knowledge Graph de-dupe
  // "JaaGa" to one company and trust facts about it.
  sameAs: [PLAY_STORE_URL, APP_STORE_URL, BLOG_URL, PARTNER_URL, ...SOCIAL_PROFILES],
});

/**
 * WebSite node with a SearchAction — enables the Google Sitelinks Search Box
 * and tells search engines the canonical site identity.
 */
export const websiteJsonLd = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  url: SITE_URL,
  name: SITE_NAME,
  description: DEFAULT_DESCRIPTION,
  inLanguage: "en-IN",
  publisher: { "@id": `${SITE_URL}/#organization` },
  potentialAction: {
    "@type": "SearchAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: `${SITE_URL}/intelligence?q={search_term_string}`,
    },
    "query-input": "required name=search_term_string",
  },
});

/** BreadcrumbList builder. `items` = [{ name, path }] in order (path is relative). */
export const breadcrumbJsonLd = (items = []) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items
    .filter((it) => it && it.name)
    .map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: toCanonicalUrl(it.path),
    })),
});

/**
 * WebApplication builder for free, browser-based tools (survey map, land
 * calculators). Price 0 INR. No aggregateRating on purpose: we have no real
 * on-page ratings, and invented ones break Google's guidelines.
 */
export const webApplicationJsonLd = ({ name, description, path, inLanguage = ["en-IN", "te-IN"] }) => ({
  "@context": "https://schema.org",
  "@type": "WebApplication",
  name,
  description,
  url: toCanonicalUrl(path),
  applicationCategory: "UtilitiesApplication",
  operatingSystem: "Web, Android, iOS",
  inLanguage,
  isAccessibleForFree: true,
  offers: { "@type": "Offer", price: "0", priceCurrency: "INR" },
  provider: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
});

/** Strip HTML tags + collapse whitespace so an answer is safe as JSON-LD text. */
const stripHtml = (s) =>
  (s ?? "")
    .toString()
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/**
 * FAQPage builder — powers Google's expandable FAQ rich result.
 * `faqs` = [{ question, answer }] (answer may contain HTML; it's stripped).
 * Returns null when there are no usable Q&A pairs (so callers can skip it).
 */
export const faqPageJsonLd = (faqs = []) => {
  const list = (Array.isArray(faqs) ? faqs : [])
    .filter((f) => f && f.question && f.answer)
    .map((f) => ({
      "@type": "Question",
      name: stripHtml(f.question),
      acceptedAnswer: { "@type": "Answer", text: stripHtml(f.answer) },
    }));
  if (!list.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: list,
  };
};

/**
 * Service builder (with an Offer when a price is known) — describes a document /
 * property service so search engines understand the offering + provider + area.
 * { name, description, path, areaServed, price } — price in INR (number or numeric string).
 */
export const serviceJsonLd = ({
  name,
  description,
  path,
  areaServed,
  price,
} = {}) => {
  if (!name) return null;
  const node = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: stripHtml(name),
    serviceType: stripHtml(name),
    url: toCanonicalUrl(path),
    provider: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
    areaServed: areaServed
      ? { "@type": "AdministrativeArea", name: stripHtml(areaServed) }
      : { "@type": "Country", name: "India" },
  };
  if (description) node.description = stripHtml(description);
  const priceNum = Number(String(price ?? "").replace(/[^0-9.]/g, ""));
  if (priceNum > 0) {
    node.offers = {
      "@type": "Offer",
      price: String(priceNum),
      priceCurrency: "INR",
      availability: "https://schema.org/InStock",
      url: toCanonicalUrl(path),
    };
  }
  return node;
};
